import { AloftArea, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from './orderSelectors.ts';
import { selectSiteById, selectSiteEntities } from './propertyDataSelectors/siteSelectors.ts';
import { selectSiteValue, selectSiteValues } from './selectSiteValue.ts';

const calculateSiteCostPerSF = (
  siteValue: ReturnType<typeof selectSiteValue>,
  site: ReturnType<typeof selectSiteById> | undefined,
) => {
  if (
    isUndefined(siteValue) ||
    isUndefined(site) ||
    isUndefined(site.area) ||
    site.area.unitAmount === 0
  ) {
    return undefined;
  }

  return siteValue.div(AloftArea.schema.parse(site.area).getUnitAmount('sqft'));
};

export const selectSiteCostPerSf = createSelector(
  [selectSiteValue, selectSiteById],
  calculateSiteCostPerSF,
);

export const selectSiteCostPerSFs = createSelector(
  [selectSiteValues, selectSiteEntities, selectCompSequence],
  (siteValues, siteEntities, compSequence) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateSiteCostPerSF(siteValues[propID], siteEntities[propID]),
      ]),
    );
  },
);

export const selectSiteCostPerSfDisplay = createSelector(
  [selectSiteCostPerSf],
  (siteValueRatio) => {
    if (isUndefined(siteValueRatio)) {
      return undefined;
    }

    return siteValueRatio.roundToDollarLike().toString(true);
  },
);

export const selectSiteCostPerSFsDisplay = createSelector(
  [selectSiteCostPerSFs],
  (siteCostPerSFs) =>
    Object.fromEntries(
      Object.entries(siteCostPerSFs).map(([propID, siteCostPerSF]) => [
        propID,
        siteCostPerSF?.toString(),
      ]),
    ),
);
